import { getBrandSiteUrl } from './organization.schema';
import type { SchemaOrgNode } from './schema-org-node.model';

export interface WebsiteSchemaData {
  /** Brand display name (e.g. "Aceable"). */
  brandName: string;
  /** Brand host used to build the WebSite `@id` (e.g. "www.aceable.com"). */
  brandUrl: string;
}

export interface WebPageSchemaData {
  /** SEO page title — emitted as `headline`. */
  pageTitle?: string;
  /** Canonical/OG page URL — emitted as `url`. */
  pageUrl?: string;
  /** SEO description — emitted as `description`. */
  pageDescription?: string;
}

/**
 * Bare Schema.org WebSite node — name + `@id` + `url`.
 *
 * Migrated from the legacy `dynamicStructuredData` script (EPS-3951) so it lands
 * in the single SchemaService `@graph` instead of a second
 * `<script type="application/ld+json">`. The `@id` is an absolute canonical URL
 * (`https://{brandHost}/#website`) so it matches the org `@id` convention — the
 * legacy output emitted a protocol-less `{brandUrl}/#website`, which is an invalid
 * IRI and inconsistent with every other node in the graph.
 */
export function getWebsiteSchema(data: WebsiteSchemaData | null): SchemaOrgNode | null {
  if (!data || !data.brandUrl || !data.brandName) return null;
  const siteUrl = getBrandSiteUrl(data.brandUrl);
  return {
    '@type': 'WebSite',
    '@id': `${siteUrl}#website`,
    name: data.brandName,
    url: siteUrl,
  };
}

/**
 * Schema.org WebPage node built from page SEO content. Only fields that are present
 * are included (mirrors the legacy dynamicStructuredData behavior). Returns null when
 * no SEO fields are available so we never emit a bare `{ '@type': 'WebPage' }` node.
 */
export function getWebPageSchema(data: WebPageSchemaData | null): SchemaOrgNode | null {
  if (!data) return null;
  const node: SchemaOrgNode = { '@type': 'WebPage' };
  if (data.pageTitle) node.headline = data.pageTitle;
  if (data.pageUrl) node.url = data.pageUrl;
  if (data.pageDescription) node.description = data.pageDescription;
  // Only return the node if it carries at least one property beyond `@type`.
  return Object.keys(node).length > 1 ? node : null;
}
